import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { SafeNavigate } from '@/services/router/safe-navigate';

const LOADING_LABEL = 'Loading…';

type MigrationRouteViewProps = {
  /** The page's rollout flag, already read by the caller. */
  isMigrated: boolean;
  /** The `journeys` twin of this URL, which the embedded Ember app serves; `null` while it resolves. */
  legacyHref: string | null;
  /**
   * Whether the page's own prerequisites have resolved (typically the current
   * workspace, which stays unsettled while the `/workspaces/last` sentinel
   * leaves route params stale). A loader holds the frame until then.
   */
  isReady?: boolean;
  children: ReactNode;
};

/**
 * The shell of a migrated route that has no Ember counterpart at its own URL:
 * flag on renders the page; flag off hands back to the `journeys` twin that
 * the embedded Ember app does serve (a rollback toggle must never strand a
 * held/bookmarked URL). The handoff replaces history, mirroring an
 * Ember-route-hook redirect.
 */
export function MigrationRouteView({ isMigrated, legacyHref, isReady = true, children }: MigrationRouteViewProps) {
  if (!isMigrated) {
    if (legacyHref == null) {
      return <PageLoader label={LOADING_LABEL} />;
    }
    return <SafeNavigate to={legacyHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  if (!isReady) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return children;
}
